/**
 * `/notifications` — the full history (SPEC `notifications` § Bell + /notifications screen).
 *
 * A 960px column: heading, an All | Unread segmented filter (default **All**, because this screen
 * is the archive; the homescreen strip is the unread half), "Mark all as read", then the same
 * notification cards at the feed measure, newest first, infinite-scrolled over the #48 cursor.
 *
 * Archived profiles' notifications stay here and stay functional — nothing new can fire for them
 * (archived profiles are not scraped), but what already fired is history and history is kept.
 */

import { useState } from 'react';
import { Link } from 'react-router';
import type { NotificationFilter } from '@lw/shared';
import {
  ChevronLeftIcon,
  EmptyState,
  ErrorState,
  Spinner,
  cx,
} from '../../components';
import { HOME_PATH } from '../../app/routes';
import { pageTitle } from '../../app/appTitle';
import { useDocumentTitle, useInView } from '../../lib/hooks';
import { useAppTimezone, useMediaEnabled } from '../settings/useSettings';
import { PostPreviewProvider, usePostPreview } from '../view/PreviewContext';
import { NotificationCard } from './NotificationCard';
import { notificationPreviewIds } from './notificationCardModel';
import { useNotificationActions, useNotificationsPage } from './useNotifications';
import './notifications.css';

const FILTERS: { id: NotificationFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
];

function NotificationsList({ filter, mediaEnabled }: { filter: NotificationFilter; mediaEnabled: boolean }) {
  const query = useNotificationsPage(filter);
  const actions = useNotificationActions();
  const { openPreview } = usePostPreview();

  const notifications = query.data?.pages.flatMap((page) => page.notifications) ?? [];
  // The popup's prev/next walk stays on this route, so only the in-place variant contributes.
  const ids = notificationPreviewIds(notifications);

  const sentinelRef = useInView<HTMLDivElement>(
    query.hasNextPage && !query.isFetchingNextPage,
    () => void query.fetchNextPage(),
    { rootMargin: '400px' },
  );

  return (
    <>
      {query.isPending ? <Spinner size="lg" label="Loading notifications…" /> : null}

      {query.isError ? (
        <ErrorState
          title="Couldn't load notifications"
          error={query.error}
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
        />
      ) : null}

      {query.isSuccess && notifications.length === 0 ? (
        filter === 'unread' ? (
          <EmptyState title="All caught up" description="Nothing unread right now." />
        ) : (
          <EmptyState
            title="No alerts yet"
            description="Alerts appear when a tracked post crosses your thresholds. Set them in Settings → Notifications."
          />
        )
      ) : null}

      <div className="notif-list">
        {/* One list, two sources, one card: it branches internally so neither surface has to. */}
        {notifications.map((notification) => (
          <NotificationCard
            key={notification.id}
            notification={notification}
            mediaEnabled={mediaEnabled}
            onOpen={(postId) => openPreview(postId, ids)}
            onMarkRead={actions.markRead}
            dismissing={actions.dismissing.has(notification.id)}
          />
        ))}
      </div>

      <div className="feed__sentinel" ref={sentinelRef}>
        {query.isFetchingNextPage ? <Spinner label="Loading more…" /> : null}
        {!query.hasNextPage && notifications.length > 0 ? (
          <span className="muted">End of the list.</span>
        ) : null}
      </div>
    </>
  );
}

export function NotificationsPage() {
  useDocumentTitle(pageTitle('Notifications'));
  const [filter, setFilter] = useState<NotificationFilter>('all');
  const mediaEnabled = useMediaEnabled();
  const timezone = useAppTimezone();
  const actions = useNotificationActions();

  return (
    <PostPreviewProvider mediaEnabled={mediaEnabled} timezone={timezone}>
      <div className="page notifications">
        <div className="content">
          <div className="section-head notif-head">
            <Link className="btn-ghost btn-ghost--sm notif-back" to={HOME_PATH}>
              <ChevronLeftIcon size={14} />
              Home
            </Link>
            <h1 className="section-title">Notifications</h1>
            <div className="section-head__actions">
              <div className="notif-seg" role="group" aria-label="Filter notifications">
                {FILTERS.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className={cx('notif-seg__btn', filter === option.id && 'notif-seg__btn--on')}
                    aria-pressed={filter === option.id}
                    onClick={() => setFilter(option.id)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="btn-ghost btn-ghost--sm"
                onClick={actions.markAllRead}
                disabled={actions.markingAll}
              >
                {actions.markingAll ? <Spinner size="sm" /> : null}
                Mark all as read
              </button>
            </div>
          </div>

          <NotificationsList filter={filter} mediaEnabled={mediaEnabled} />
        </div>
      </div>
    </PostPreviewProvider>
  );
}

export default NotificationsPage;
